import { editor } from "monaco-editor/esm/vs/editor/editor.api";
import { useEffect, useMemo, useRef, useState } from "react";
import { CodeEditorOptions, CodeEditorStatus } from "../../types/CodeEditor";
import { TypeDefinitionOpener } from "./adapters/TypeDefinitionOpener";
import styles from "./CodeEditor.module.css";
import { codeEditorInstance } from "./CodeEditorInstance";
import { useEditorFocusMode } from "./hooks/useEditorFocusMode";
import { useEditorViewState } from "./hooks/useEditorViewState";
import { MessageOverlay } from "./message-overlay/MessageOverlay";
import { LanguageService } from "./services/LanguageService";
import { revalidateModels } from "./services/revalidateModels";
import { setTheme } from "./services/Themes";
import { StatusOverlay } from "./status-overlay/StatusOverlay";
import "@vscode/codicons/dist/codicon.css";

export const CodeEditor = (props: CodeEditorOptions) => {
  const {
    filePath,
    theme,
    readOnly,
    selection,
    focusMode,
    showMinimap,
    showlineNumbers,
    codeFolding,
    onEditorMounted,
    onDefinitionOpened,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [codeEditor, setCodeEditor] = useState<editor.IStandaloneCodeEditor>();
  const [tsStatus, setTsStatus] = useState<CodeEditorStatus>("loading");

  const typeDefinitionOpener = useMemo(() => new TypeDefinitionOpener(onDefinitionOpened), [onDefinitionOpened]);

  // Expiremental
  setTheme(theme);

  // Initialize monaco-editor
  useEffect(() => {
    if (!ref.current)
      return;

    setCodeEditor((state) => {
      if (state)
        return;

      console.debug("Creating new instance of monaco-editor");

      editor.registerEditorOpener(typeDefinitionOpener);
      LanguageService.setTypeScriptOptions();

      codeEditorInstance.instance = editor.create(ref.current!, {
        codeLens: true,
        fixedOverflowWidgets: true,
        automaticLayout: true,
        readOnly,
        fontSize: 14,
        minimap: {
          enabled: !(showMinimap === false),
          autohide: true
        },
        lineNumbers: showlineNumbers === false ? "off" : "on",
        lineDecorationsWidth: showlineNumbers === false ? 0 : 10,
        smoothScrolling: true,
        scrollBeyondLastLine: false,
        folding: !(codeFolding === false),
      });

      return codeEditorInstance.instance;
    });

    return () => {
      codeEditorInstance.instance = undefined;
      codeEditor?.dispose();
    }
  }, []);

  // Expose editor instance via callback
  useEffect(() => {
    if (onEditorMounted && codeEditor)
      onEditorMounted(codeEditorInstance);

  }, [onEditorMounted, codeEditor])

  useEffect(() => {
    const subscription = LanguageService.onTypeDefinitionsLoaded(() => {
      setTsStatus("checking");
      console.time("revalidateModels")
      revalidateModels().then(() => {
        setTsStatus(undefined);
        console.timeEnd("revalidateModels")
      });
    })

    return () => {
      subscription?.dispose();
    }
  }, [])

  // Open file for viewing
  const viewMode = useEditorViewState(codeEditor, filePath);

  // Apply focus mode
  useEditorFocusMode(codeEditor, filePath, selection, readOnly, focusMode);

  return (
    <div className={styles.codeEditor}>
      {viewMode === "blank" && <MessageOverlay />}
      {viewMode === "notFound" && <MessageOverlay message="File was not found" filePath={filePath} />}
      {viewMode === "noPreview" && <MessageOverlay message="Preview is not available" filePath={filePath} />}
      {viewMode === "unexpected" && <MessageOverlay message="Something unexpected happened. Preview is not available" />}
      <StatusOverlay status={tsStatus} />
      <div id="code-editor" className={styles.monacoEditor} ref={ref} />
    </div>
  )
}
